import { useSuspenseQuery } from "@apollo/client";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import InsertInvitationIcon from "@mui/icons-material/InsertInvitation";
import UploadFile from "@mui/icons-material/UploadFile";
import {
  Box,
  Button,
  Checkbox,
  Chip,
  Divider,
  FormControl,
  FormHelperText,
  InputLabel,
  List,
  ListItem,
  Menu,
  MenuItem,
  Select,
  Stack,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import {
  DateRangePicker,
  LocalizationProvider,
  MultiInputDateRangeField,
  PickersShortcutsProps,
} from "@mui/x-date-pickers-pro";
import { DateRange } from "@mui/x-date-pickers-pro/models";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { mdHeight, SubNavAppBar, xsHeight } from "components/AppBar";
import { tabsOffset } from "components/TabLinks";
import { parseISO } from "date-fns";
import { FeatureFlagKey } from "encamp-shared/src/featureFlags";
import { gql } from "generated-graphql";
import { FacilityWasteView } from "generated-graphql/graphql";
import { useFeatureFlags } from "hooks/useFeatureFlags";
import { useQueueManifestDocumentParse } from "hooks/useQueueManifestDocumentParse";
import { useTenant } from "hooks/useTenant";
import { DateTime } from "luxon";
import { useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { prettyPrintDateMed } from "util/dates";

import { ManifestUploadDialog } from "../../Manifests/ManifestUploadDialog";
import { prettyPrintWasteViewType } from "./prettyPrintWasteViewType";
import { useWasteFacility } from "./useWasteFacility";
import { useWasteFacilityState } from "./WasteFacilityContext";

type GraphType = "hazardousWaste" | "hazardousWasteByMonth" | "highestGeneratingWasteStreams";

export type Filter = {
  graphType: GraphType;
  startDate?: string;
  endDate?: string;
  tags?: string[];
  view?: FacilityWasteView;
};

const TAGS_QUERY = gql(/* GraphQL */ `
  query WasteTags(
    $search: String
    $page: Int
    $pageSize: Int
    $sort: [SortModel!]
  ) {
    tags(search: $search, page: $page, pageSize: $pageSize, sort: $sort) {
      items {
        id
        name
      }
      count
    }
  }
`);

export function WasteFacilityFilters() {
  const theme = useTheme();
  const { featureFlags } = useFeatureFlags();
  const { tenantId } = useTenant();
  const { facilityId } = useParams<{ facilityId: string }>();
  const initialLoad = useRef(true);
  const isLeftNavV2 = featureFlags[FeatureFlagKey.LeftNavV2];
  const { canQueueManifestParse } = useQueueManifestDocumentParse();
  const [manifestUploadDialogOpen, setManifestUploadDialogOpen] = useState(false);

  const [defaultDateRange] = useState<{
    startDate: string;
    endDate: string;
  }>(() => {
    const currentYear = new Date().getFullYear();
    const startDate = new Date(currentYear, 0, 1).toISOString().slice(0, 10);
    const endDate = new Date(currentYear, 11, 31).toISOString().slice(0, 10);
    return { startDate, endDate };
  });

  const defaultFilters = useMemo(() => {
    return {
      startDate: defaultDateRange.startDate,
      endDate: defaultDateRange.endDate,
      view: FacilityWasteView.All,
    };
  }, [defaultDateRange]);

  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);

  const {
    wasteFacilityState: { optimisticFilters },
    setWasteFacilityState,
  } = useWasteFacilityState();

  const { data: tagsData } = useSuspenseQuery(TAGS_QUERY, {
    variables: {
      search: `tenantId:${tenantId} type:Waste`,
      page: 0,
      pageSize: 100,
      sort: [{ field: "name", sort: "asc" }],
    },
  });

  const { data: facilityData } = useWasteFacility(facilityId);

  const tagOptions = useMemo(() => tagsData?.tags?.items ?? [], [tagsData]);

  // Use optimistic filters for immediate UI feedback
  const { tags, startDate, endDate, view } = optimisticFilters;

  const hasAnyFilters = useMemo(() => {
    return Object.values(optimisticFilters).some(Boolean);
  }, [optimisticFilters]);

  // set the default filters if they, or any other filters, are not set
  useEffect(() => {
    if (!hasAnyFilters && initialLoad.current) {
      setWasteFacilityState(() => defaultFilters);
      initialLoad.current = false;
    }
  }, [hasAnyFilters, setWasteFacilityState, defaultFilters]);

  // prevent the user from changing the date range outside of the current year
  // and prevent end date from going past the end of the current month
  useEffect(() => {
    if (startDate && endDate) {
      const start = new Date(startDate);
      const end = new Date(endDate);
      const diff = end.getTime() - start.getTime();
      const diffInDays = diff / (1000 * 60 * 60 * 24);

      // Get the last day of the current month
      const now = new Date();
      const lastDayOfCurrentMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0);
      // If end date is beyond the current month, cap it to the last day of current month
      if (end > lastDayOfCurrentMonth) {
        setWasteFacilityState((f) => {
          f.endDate = lastDayOfCurrentMonth.toISOString().slice(0, 10);
        });
        return;
      }

      if (diffInDays >= 366) {
        // Use Luxon to safely add 1 year (automatically handles leap year edge cases)
        const startDateTime = DateTime.fromJSDate(start);
        const newEndDateTime = startDateTime.plus({ years: 1 });

        setWasteFacilityState((f) => {
          f.endDate = newEndDateTime.toISODate();
        });
      }
    }
  }, [startDate, endDate, setWasteFacilityState, defaultDateRange]);

  // Handlers
  const handleViewChange = (newView: FacilityWasteView) => {
    setWasteFacilityState((f) => {
      f.view = newView;
    });
  };

  const handleDateRangeChange = (newValue: [Date | null, Date | null]) => {
    const toIsoString = (d: Date | null): string | undefined => {
      if (!d) return undefined;
      if (isNaN(d.getTime())) return undefined;
      try {
        return d.toISOString().slice(0, 10);
      } catch {
        return undefined;
      }
    };

    setWasteFacilityState((f) => {
      f.startDate = toIsoString(newValue[0]);
      f.endDate = toIsoString(newValue[1]);
    });
  };

  // Helper text logic for date range
  const safeParseDate = (iso?: string): Date | null => {
    if (!iso) return null;
    const d = parseISO(iso);
    return isNaN(d.getTime()) ? null : d;
  };

  const start = safeParseDate(startDate);
  const end = safeParseDate(endDate);

  let dateHelperText: string | null = null;

  if (!start || !end) {
    dateHelperText = "Select a start and end date to view data";
  }

  const standardInputProps = {
    size: "small" as const,
    fullWidth: true,
    sx: {
      "& .MuiInputBase-root": {
        height: "40px", // Standardize height across all inputs
      },
    },
  };

  const facility = facilityData.facility;

  return (
    <SubNavAppBar
      sx={{
        px: theme.spacing(2),
        py: theme.spacing(3),
        borderBottom: "1px solid",
        borderColor: theme.palette.divider,
        justifyContent: "space-between",
        flexDirection: { lg: "row" },
        gap: 1,
        ...(!isLeftNavV2 && {
          top: {
            xs: theme.spacing(xsHeight + tabsOffset),
            md: theme.spacing(mdHeight + tabsOffset),
          },
        }),
      }}
    >
      <Stack direction={{ xs: "column", md: "row" }} alignItems="start" spacing={2}>
        {/* Date Range Picker */}
        <FormControl sx={{ minWidth: 300 }} size="small" variant="standard">
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <DateRangePicker
              calendars={2}
              onAccept={handleDateRangeChange}
              value={[start, end]}
              localeText={{
                start: "Start Date",
                end: "End Date",
              }}
              slots={{
                field: MultiInputDateRangeField,
                shortcuts: HorizontalShortcuts,
              }}
              slotProps={{
                textField: {
                  ...standardInputProps,
                  fullWidth: true,
                  variant: "standard",
                  size: "small",
                  inputProps: {
                    autoComplete: "off",
                    "data-1p-ignore": "true",
                    "data-lpignore": "true",
                    "data-form-type": "other",
                  },
                  InputProps: {
                    endAdornment: <InsertInvitationIcon />,
                  },
                },
                shortcuts: { items: datePickerShortcut },
              }}
            />
          </LocalizationProvider>
          {dateHelperText && <FormHelperText>{dateHelperText}</FormHelperText>}
        </FormControl>

        {/* View Filter */}
        <FormControl
          sx={{ ...standardInputProps.sx, width: { xs: "100%", md: 210 } }}
          variant="standard"
        >
          <InputLabel id="view-filter-label">View</InputLabel>
          <Select
            labelId="view-filter-label"
            value={view || FacilityWasteView.All}
            onChange={(e) => handleViewChange(e.target.value)}
            {...standardInputProps}
            label="View"
          >
            <MenuItem value={FacilityWasteView.All}>
              {prettyPrintWasteViewType(FacilityWasteView.All)}
            </MenuItem>
            <MenuItem value={FacilityWasteView.Reportable}>
              {prettyPrintWasteViewType(FacilityWasteView.Reportable)}
            </MenuItem>
            <MenuItem value={FacilityWasteView.NotReportable}>
              {prettyPrintWasteViewType(FacilityWasteView.NotReportable)}
            </MenuItem>
          </Select>
        </FormControl>

        {/* Tags */}
        <Box sx={{ alignItems: "end" }}>
          <Chip
            label={
              <Stack direction="row" alignItems="center" spacing={0.5}>
                <span>{(tags?.length ?? 0) > 0 ? `Tags (${tags?.length ?? 0})` : "Tags"}</span>
                <ExpandMoreIcon fontSize="small" />
              </Stack>
            }
            color={menuAnchor ? "primary" : "default"}
            variant="filled"
            sx={{
              ...standardInputProps.sx,
              mt: { xs: 0, md: "20px" },
            }}
            onClick={(e) => setMenuAnchor(e.currentTarget)}
            clickable
            size="medium"
          />
          <Menu
            anchorEl={menuAnchor}
            open={Boolean(menuAnchor)}
            onClose={() => setMenuAnchor(null)}
          >
            {tagOptions.length === 0 && <MenuItem disabled={true}>No tags available</MenuItem>}
            {tagOptions.length > 0 && (
              <MenuItem
                value="select-all"
                onClick={() => {
                  const isAllSelected = (tags?.length ?? 0) === tagOptions.length;
                  setWasteFacilityState((f) => {
                    f.tags = isAllSelected ? [] : tagOptions.map((tag) => tag.name);
                  });
                  setMenuAnchor(null);
                }}
              >
                <Checkbox
                  checked={tags?.length === tagOptions.length}
                  indeterminate={(tags?.length ?? 0) > 0 && (tags?.length ?? 0) < tagOptions.length}
                />
                Select All
              </MenuItem>
            )}
            {tagOptions.map((option) => (
              <MenuItem
                key={option.id}
                value={option.name}
                onClick={() => {
                  const isSelected = tags?.includes(option.name);
                  setWasteFacilityState((f) => {
                    const current = new Set(f.tags ?? []);
                    if (isSelected) {
                      current.delete(option.name);
                    } else {
                      current.add(option.name);
                    }
                    f.tags = Array.from(current);
                  });
                }}
              >
                <Checkbox checked={!!tags?.includes(option.name)} /> {option.name}
              </MenuItem>
            ))}
          </Menu>
        </Box>
      </Stack>
      <Stack alignItems="flex-end" spacing={0.5}>
        {canQueueManifestParse && (
          <Button
            variant="outlined"
            color="warning"
            size="small"
            startIcon={<UploadFile />}
            onClick={() => setManifestUploadDialogOpen(true)}
          >
            Upload Manifest PDF
          </Button>
        )}
        <Tooltip
          title={
            facility?.mostRecentVendorManifestShippedDate
              ? `Vendor: most recent shipment for this facility ${prettyPrintDateMed(
                  facility?.mostRecentVendorManifestShippedDate
                )}`
              : ""
          }
        >
          <Typography variant="caption" fontStyle="italic">
            {facility?.vendorWasteManifestLastUpdated
              ? `Vendor data updated ${prettyPrintDateMed(facility.vendorWasteManifestLastUpdated)}`
              : "No vendor data for this facility"}
          </Typography>
        </Tooltip>

        <Tooltip
          title={
            facility?.mostRecentEManifestShippedDate
              ? `e‑Manifest: most recent shipment for this facility ${prettyPrintDateMed(
                  facility?.mostRecentEManifestShippedDate
                )}`
              : ""
          }
        >
          <Typography variant="caption" fontStyle="italic">
            {facility?.eManifestLastUpdated
              ? `e‑Manifest data updated ${prettyPrintDateMed(facility.eManifestLastUpdated)}`
              : "No e‑Manifest data for this facility"}
          </Typography>
        </Tooltip>
      </Stack>
      {canQueueManifestParse && (
        <ManifestUploadDialog
          open={manifestUploadDialogOpen}
          onClose={() => setManifestUploadDialogOpen(false)}
          tenantId={tenantId ?? ""}
          facilityId={facilityId}
        />
      )}
    </SubNavAppBar>
  );
}

const datePickerShortcut = [
  {
    label: `${new Date().getFullYear()}`,
    getValue: () => {
      const today = new Date();
      const start = new Date(today.getFullYear(), 0, 1);

      const end = new Date(today.getFullYear(), today.getMonth() + 1, 0);
      return [start, end];
    },
  },
  {
    label: `${new Date().getFullYear() - 1}`,
    getValue: () => {
      const today = new Date();
      const start = new Date(today.getFullYear() - 1, 0, 1);
      const end = new Date(today.getFullYear() - 1, 11, 31);
      return [start, end];
    },
  },
  {
    label: `${new Date().getFullYear() - 2}`,
    getValue: () => {
      const today = new Date();
      const start = new Date(today.getFullYear() - 2, 0, 1);
      const end = new Date(today.getFullYear() - 2, 11, 31);
      return [start, end];
    },
  },
  {
    label: "Last Quarter",
    getValue: () => {
      const today = new Date();
      const currentQuarter = Math.floor(today.getMonth() / 3);
      const lastQuarter = currentQuarter === 0 ? 3 : currentQuarter - 1;
      const lastQuarterYear = currentQuarter === 0 ? today.getFullYear() - 1 : today.getFullYear();
      const start = new Date(lastQuarterYear, lastQuarter * 3, 1);
      const end = new Date(lastQuarterYear, (lastQuarter + 1) * 3, 0);
      return [start, end];
    },
  },
  {
    label: "This Quarter",
    getValue: () => {
      const today = new Date();
      const currentQuarter = Math.floor(today.getMonth() / 3);
      const start = new Date(today.getFullYear(), currentQuarter * 3, 1);
      const end = new Date(today.getFullYear(), (currentQuarter + 1) * 3, 0);
      return [start, end];
    },
  },
  {
    label: "Reset",
    getValue: () => [null, null],
  },
];

function HorizontalShortcuts(props: PickersShortcutsProps<DateRange<Date>>) {
  const { items, onChange, isValid, changeImportance = "accept" } = props;

  if (items == null || items.length === 0) {
    return null;
  }

  const resolvedItems = items.map((item) => {
    const newValue = item.getValue({ isValid });

    return {
      label: item.label,
      onClick: () => {
        onChange(newValue, changeImportance, item);
      },
      disabled: !isValid(newValue),
    };
  });

  return (
    <Box
      sx={{
        width: "100%",
        gridRow: 1,
        gridColumn: 2,
      }}
    >
      <List
        dense
        sx={(theme) => ({
          display: "flex",
          justifyContent: "center",
          px: theme.spacing(2),
          "& .MuiListItem-root": {
            width: "inherit",
            py: 0,
            px: theme.spacing(0.5),
          },
        })}
      >
        {resolvedItems.map((item) => {
          return (
            <ListItem key={item.label}>
              <Chip {...item} />
            </ListItem>
          );
        })}
      </List>
      <Divider />
    </Box>
  );
}
